import { raceEventMode } from './race-events.js';
import type {
  ArcadePreset,
  MapDefinition,
  NpcDifficulty,
  RaceEventMode,
  RaceSettings,
  RoomVisibility,
  TimeOfDay,
  Weather,
} from './types.js';
import { isWeather } from './types.js';

/** Hard grid limits shared by the lobby UI, validation and the race core. */
export const MAX_LOBBY_NPCS = 11;
export const MAX_LOBBY_PLAYERS = 6;
export const MIN_LOBBY_PLAYERS = 2;
export const MIN_CUSTOM_LAPS = 1;
export const MAX_CUSTOM_LAPS = 10;
export const NPC_DIFFICULTY_ORDER: readonly NpcDifficulty[] = ['rookie', 'sport', 'pro'];

const EVENT_MODES = new Set<RaceEventMode>([
  'circuit', 'sprint', 'time-trial', 'style-rush', 'elimination', 'heat-run', 'road-rage',
]);
const TIMES = new Set<TimeOfDay>(['dawn', 'day', 'sunset', 'night']);
const DIFFICULTIES = new Set<NpcDifficulty>(NPC_DIFFICULTY_ORDER);
const ARCADE_PRESETS = new Set<ArcadePreset>(['standard', 'nitro-rush', 'heavy-metal', 'traffic-panic', 'low-gravity']);
const VISIBILITIES = new Set<RoomVisibility>(['private', 'public']);

/**
 * One host edit in the lobby. Every field is optional; `null` for an
 * atmosphere or lap field restores the selected track's authored default.
 */
export interface LobbySettingsPatch {
  mapId?: string;
  eventMode?: RaceEventMode;
  laps?: number | null;
  timeOfDay?: TimeOfDay | null;
  weather?: Weather | null;
  traffic?: boolean;
  collisions?: boolean;
  antiCheat?: boolean;
  /** Complete ordered rival grid, one difficulty per NPC slot. */
  npcRoster?: NpcDifficulty[];
  visibility?: RoomVisibility;
  maxPlayers?: number;
  /** Offline-only playful rule set; online hosts always race standard. */
  arcadePreset?: ArcadePreset;
}

/** Rival grid in slot order, also for rooms created before per-slot difficulty. */
export function npcRosterOf(settings: Pick<RaceSettings, 'npcCount' | 'npcDifficulty' | 'npcRoster'>): NpcDifficulty[] {
  const count = Math.max(0, Math.min(MAX_LOBBY_NPCS, Math.floor(settings.npcCount)));
  const roster = settings.npcRoster;
  return Array.from({ length: count }, (_, index) => {
    const value = roster?.[index];
    return value && DIFFICULTIES.has(value) ? value : settings.npcDifficulty;
  });
}

/** The difficulty most of the grid races at; ties favour the gentler one so
 * career rewards never pay for a single token pro rival. */
export function dominantNpcDifficulty(roster: readonly NpcDifficulty[], fallback: NpcDifficulty = 'rookie'): NpcDifficulty {
  if (roster.length === 0) return fallback;
  let best: NpcDifficulty = roster[0]!;
  let bestCount = -1;
  for (const difficulty of NPC_DIFFICULTY_ORDER) {
    const count = roster.filter((entry) => entry === difficulty).length;
    if (count > bestCount) {
      best = difficulty;
      bestCount = count;
    }
  }
  return best;
}

export function minimumCompetitorsFor(mode: RaceEventMode): number {
  return mode === 'elimination' || mode === 'road-rage' ? 2 : 1;
}

/** Whether laps are a meaningful host choice for this event format. */
export function lapsConfigurable(mode: RaceEventMode): boolean {
  return mode === 'circuit' || mode === 'elimination' || mode === 'time-trial' || mode === 'style-rush';
}

/**
 * Pure, shared settings transition. The server, the in-browser offline lobby
 * and tests all apply host edits through this one function so the rules (a
 * time trial has no rivals, Road Rage always collides, a new track restores
 * its own atmosphere) cannot drift between them.
 */
export function applyLobbyPatch(
  current: RaceSettings,
  patch: LobbySettingsPatch,
  map: MapDefinition,
  options: { online: boolean } = { online: true },
): RaceSettings {
  const next: RaceSettings = { ...current };
  const mapChanged = patch.mapId !== undefined && patch.mapId !== current.mapId;
  if (mapChanged) {
    next.mapId = map.id;
    next.timeOfDay = map.timeOfDay;
    next.weather = map.weather;
    next.laps = undefined;
  }
  if (patch.eventMode !== undefined) {
    next.eventMode = patch.eventMode;
    next.timeTrial = patch.eventMode === 'time-trial';
  }
  if (patch.laps !== undefined) next.laps = patch.laps === null ? undefined : patch.laps;
  if (patch.timeOfDay !== undefined) next.timeOfDay = patch.timeOfDay ?? map.timeOfDay;
  if (patch.weather !== undefined) next.weather = patch.weather ?? map.weather;
  if (patch.traffic !== undefined) next.traffic = patch.traffic;
  if (patch.collisions !== undefined) next.collisions = patch.collisions;
  if (patch.antiCheat !== undefined) next.antiCheat = patch.antiCheat;
  if (patch.visibility !== undefined) next.visibility = patch.visibility;
  if (patch.maxPlayers !== undefined) next.maxPlayers = patch.maxPlayers;
  if (patch.arcadePreset !== undefined) next.arcadePreset = patch.arcadePreset;
  let roster = patch.npcRoster ? [...patch.npcRoster] : npcRosterOf(current);

  const mode = raceEventMode(next);
  if (mode === 'time-trial') roster = [];
  if (mode === 'road-rage') next.collisions = true;
  if (!lapsConfigurable(mode)) next.laps = undefined;
  if (!options.online) next.maxPlayers = 1;
  if (options.online) {
    next.arcadePreset = 'standard';
    next.maxPlayers = Math.max(MIN_LOBBY_PLAYERS, Math.min(MAX_LOBBY_PLAYERS, Math.floor(next.maxPlayers || MAX_LOBBY_PLAYERS)));
  }
  next.npcRoster = roster.slice(0, MAX_LOBBY_NPCS);
  next.npcCount = next.npcRoster.length;
  next.npcDifficulty = dominantNpcDifficulty(next.npcRoster, current.npcDifficulty);

  // Curated events (daily, tours, nemesis, wagers) stop being that event as
  // soon as the race itself changes. Visibility alone keeps them intact.
  const raceChanged = Object.keys(patch).some((key) => key !== 'visibility' && key !== 'maxPlayers');
  if (raceChanged) {
    next.dailyChallengeId = undefined;
    next.tourId = undefined;
    next.tourStage = undefined;
    next.featuredRival = undefined;
    next.wager = undefined;
  }
  return next;
}

/** Strict structural validation for untrusted lobby edits. */
export function parseLobbySettingsPatch(value: unknown): LobbySettingsPatch {
  if (!isRecord(value)) throw new Error('Lobby settings missing');
  const patch: LobbySettingsPatch = {};
  for (const key of Object.keys(value)) {
    if (!PATCH_KEYS.has(key)) throw new Error(`Unknown lobby setting ${key}`);
  }
  if (value.mapId !== undefined) {
    if (typeof value.mapId !== 'string' || !/^[a-z0-9-]{1,64}$/i.test(value.mapId)) throw new Error('Invalid map');
    patch.mapId = value.mapId;
  }
  if (value.eventMode !== undefined) {
    if (!EVENT_MODES.has(value.eventMode as RaceEventMode)) throw new Error('Invalid event format');
    patch.eventMode = value.eventMode as RaceEventMode;
  }
  if (value.laps !== undefined) {
    if (value.laps !== null && !integerInRange(value.laps, MIN_CUSTOM_LAPS, MAX_CUSTOM_LAPS)) throw new Error('Invalid lap count');
    patch.laps = value.laps as number | null;
  }
  if (value.timeOfDay !== undefined) {
    if (value.timeOfDay !== null && !TIMES.has(value.timeOfDay as TimeOfDay)) throw new Error('Invalid time of day');
    patch.timeOfDay = value.timeOfDay as TimeOfDay | null;
  }
  if (value.weather !== undefined) {
    if (value.weather !== null && !isWeather(value.weather)) throw new Error('Invalid weather');
    patch.weather = value.weather as Weather | null;
  }
  for (const key of ['traffic', 'collisions', 'antiCheat'] as const) {
    if (value[key] === undefined) continue;
    if (typeof value[key] !== 'boolean') throw new Error(`Invalid ${key}`);
    patch[key] = value[key];
  }
  if (value.npcRoster !== undefined) {
    if (
      !Array.isArray(value.npcRoster)
      || value.npcRoster.length > MAX_LOBBY_NPCS
      || !value.npcRoster.every((entry) => DIFFICULTIES.has(entry as NpcDifficulty))
    ) throw new Error('Invalid rival grid');
    patch.npcRoster = [...value.npcRoster] as NpcDifficulty[];
  }
  if (value.visibility !== undefined) {
    if (!VISIBILITIES.has(value.visibility as RoomVisibility)) throw new Error('Invalid visibility');
    patch.visibility = value.visibility as RoomVisibility;
  }
  if (value.maxPlayers !== undefined) {
    if (!integerInRange(value.maxPlayers, MIN_LOBBY_PLAYERS, MAX_LOBBY_PLAYERS)) throw new Error('Invalid player limit');
    patch.maxPlayers = value.maxPlayers as number;
  }
  if (value.arcadePreset !== undefined) {
    if (!ARCADE_PRESETS.has(value.arcadePreset as ArcadePreset)) throw new Error('Invalid rule set');
    patch.arcadePreset = value.arcadePreset as ArcadePreset;
  }
  if (Object.keys(patch).length === 0) throw new Error('Empty lobby edit');
  return patch;
}

const PATCH_KEYS = new Set([
  'mapId', 'eventMode', 'laps', 'timeOfDay', 'weather', 'traffic', 'collisions',
  'antiCheat', 'npcRoster', 'visibility', 'maxPlayers', 'arcadePreset',
]);

export function isRoomVisibility(value: unknown): value is RoomVisibility {
  return VISIBILITIES.has(value as RoomVisibility);
}

export function isNpcDifficulty(value: unknown): value is NpcDifficulty {
  return DIFFICULTIES.has(value as NpcDifficulty);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function integerInRange(value: unknown, min: number, max: number): boolean {
  return Number.isInteger(value) && Number(value) >= min && Number(value) <= max;
}
